import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "../lib/utils"

const Tabs = TabsPrimitive.Root

type TabsListVariant = "default" | "pill" | "underline" | "numbered" | "boxed"

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: TabsListVariant
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = "default", ...props }, ref) => {
  // Define variant-specific styles
  const variantStyles: Record<TabsListVariant, string> = {
    default: "rounded-md bg-bg-100/50 p-1 inline-flex h-10",
    pill: "w-full max-w-md mx-auto h-auto bg-muted-foreground/20 p-2 rounded-full flex flex-wrap justify-center gap-1 overflow-x-auto",
    underline: "flex w-full bg-transparent p-0 h-auto border-b border-stroke-400 justify-start gap-4 overflow-x-auto scrollbar-hide",
    numbered: "flex w-full bg-transparent p-0 h-auto border-b border-stroke-400 justify-start gap-6 overflow-x-auto scrollbar-hide",
    boxed: "w-full grid grid-cols-2"
  }

  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        "items-center justify-center text-muted-foreground",
        variantStyles[variant],
        className
      )}
      {...props}
    />
  )}
)
TabsList.displayName = TabsPrimitive.List.displayName

// Extend the props interface to handle data attributes
interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  variant?: TabsListVariant
  index?: number | string
  "data-state"?: "active" | "inactive"
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant = "default", index, ...props }, ref) => {
  // Define variant-specific styles
  const variantStyles: Record<TabsListVariant, string> = {
    default: "rounded-sm data-[state=active]:bg-primary data-[state=active]:text-white-both-modes data-[state=active]:shadow-sm",
    pill: "rounded-full data-[state=active]:bg-bg-500 data-[state=active]:text-black dark:text-foreground text-xs data-[state=active]:shadow-sm",
    underline: "px-4 bg-transparent py-2 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary",
    numbered: "px-0 bg-transparent py-2 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary",
    boxed: "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
  }

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center cursor-pointer whitespace-nowrap px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {variant === "numbered" && index !== undefined ? (
        <span className="flex items-center">
          <span className={cn(
            "text-base font-medium mr-1",
            props["data-state"] === "active" ? "text-primary font-semibold" : "text-muted-foreground"
          )}>
            {index}
          </span>
          <span className="text-muted-foreground text-base font-medium">
            {props.children}
          </span>
        </span>
      ) : props.children}
    </TabsPrimitive.Trigger>
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
